import { createSelector } from 'reselect';
import { Project } from '../../models/Projects';
import { State } from '../../reducers/State';
import {
  organizationSelector,
  organizationProviderIdSelector,
  organizationSlugSelector,
} from '../Organization';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import URI from 'urijs';
import { projectNameFromRouteSelector } from '../../selectors/Router';

const getCurrentProject = (
  projects: Project[] | undefined,
  projectName: string | null
): Project | undefined => {
  return projects?.find((p) => p.name === projectName);
};

export const projectSelector = createSelector(
  ownerProjectsSelector,
  projectNameFromRouteSelector,
  getCurrentProject
);

export const projectsByNameSelector = createSelector(
  ownerProjectsSelector,
  (projects?: Project[]): Record<string, Project> | undefined =>
    projects?.reduce(
      (prev, current) => ({ ...prev, [current.name]: current }),
      {}
    )
);

export function projectNameSelector(state: State): string | undefined {
  return projectSelector(state)?.name;
}

export function ownerProjectsSelector(state: State): Project[] | undefined {
  const organization = organizationSelector(state);

  if (!organization?.uuid || !state.projects) {
    return undefined;
  }
  const key = `${organization.providerId}:${organization.slug}`;

  return state.projects?.byOrganization[key]?.last_activity_at;
}

export const numProjectsSelector = createSelector(
  ownerProjectsSelector,
  (projects: Project[] | undefined) => {
    return projects?.length ?? 0;
  }
);

export function getProjectHref(
  providerId?: number,
  slug?: string,
  name?: string
): string | undefined {
  if (!providerId || !slug || !name) {
    return undefined;
  }

  return URI('/dash/projects/')
    .segment(providerId.toString())
    .segment(slug)
    .segmentCoded(name)
    .toString();
}

export function getProjectSettingsHref(
  providerId?: number,
  slug?: string,
  name?: string
): string | undefined {
  if (!providerId || !slug || !name) {
    return undefined;
  }

  return URI('/dash/projects/')
    .segment(providerId.toString())
    .segment(slug)
    .segmentCoded(name)
    .segment('settings')
    .toString();
}

export function getProjectsSettingsHref(
  providerId?: number,
  slug?: string,
  projects?: Project[]
): { [key: string]: string | undefined } | undefined {
  if (!providerId || !slug || !projects) {
    return undefined;
  }

  const projectsByUUID = keyBy(projects, (project) => project.uuid);
  return mapValues(projectsByUUID, (project) =>
    getProjectSettingsHref(providerId, slug, project.name)
  );
}

export function getProjectAdvancedSettingsHref(
  projectSettingsHref?: string
): string | undefined {
  if (!projectSettingsHref) {
    return undefined;
  }

  return URI(projectSettingsHref).segment('advanced').toString();
}

export const projectSettingsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  projectNameSelector,
  getProjectSettingsHref
);

export const projectToProjectSettingsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  ownerProjectsSelector,
  projectNameSelector,
  getProjectsSettingsHref
);

export const projectAdvancedSettingsHrefSelector = createSelector(
  projectSettingsHrefSelector,
  getProjectAdvancedSettingsHref
);

export function getProjectNotesHref(
  providerId?: number,
  slug?: string,
  name?: string
): string | undefined {
  if (!providerId || !slug || !name) {
    return undefined;
  }

  return URI('/dash/projects/')
    .segment(providerId.toString())
    .segment(slug)
    .segmentCoded(name)
    .segment('about')
    .toString();
}

export const projectNotesHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  projectNameSelector,
  getProjectNotesHref
);

export const projectHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  projectNameSelector,
  getProjectHref
);

export function getProjectTodosHref(
  providerId?: number,
  slug?: string,
  name?: string
): string | undefined {
  if (!providerId || !slug || !name) {
    return undefined;
  }
  return URI('/dash/projects/')
    .segment(providerId.toString())
    .segment(slug)
    .segmentCoded(name)
    .segment('notes')
    .toString();
}

export const projectTodosHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  projectNameSelector,
  getProjectTodosHref
);

export function getProjectActiveTodosHref(
  projectTodosHref?: string
): string | undefined {
  if (!projectTodosHref) {
    return undefined;
  }

  return URI(projectTodosHref).segment('active').toString();
}

export const projectActiveTodosHrefSelector = createSelector(
  projectTodosHrefSelector,
  getProjectActiveTodosHref
);

export function getProjectResolvedTodosHref(
  projectTodosHref?: string
): string | undefined {
  if (!projectTodosHref) {
    return undefined;
  }

  return URI(projectTodosHref).segment('resolved').toString();
}

export const projectResolvedTodosHrefSelector = createSelector(
  projectTodosHrefSelector,
  getProjectResolvedTodosHref
);

export function getProjectReviewsHref(
  providerId?: number,
  slug?: string,
  name?: string
): string | undefined {
  if (!providerId || !slug || !name) {
    return undefined;
  }

  return URI('/dash/projects/')
    .segment(providerId.toString())
    .segment(slug)
    .segmentCoded(name)
    .segment('reviews')
    .toString();
}

export const projectReviewsHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  projectNameSelector,
  getProjectReviewsHref
);

export function getProjectStaticConfigHref(
  providerId?: number,
  slug?: string,
  name?: string
): string | undefined {
  if (!providerId || !slug || !name) {
    return undefined;
  }

  return URI('/dash/projects/')
    .segment(providerId.toString())
    .segment(slug)
    .segmentCoded(name)
    .segment('static-config')
    .toString();
}

export const projectStaticConfigHrefSelector = createSelector(
  organizationProviderIdSelector,
  organizationSlugSelector,
  projectNameSelector,
  getProjectStaticConfigHref
);

export const isSyncing = (project?: Project): boolean => {
  return !project?.display_benchmarks;
};

export const isSyncingSelector = createSelector(projectSelector, isSyncing);

export const isUpdatingSelector = (state: State) => state.projects?.isUpdating;
